import { type ScenarioPassCriteria } from 'components/scenarios/concurrent/concurrent-scenario-form-utils'

import type { CamelCasedProperties } from 'type-fest'
import type { RequestSource } from 'types/api/gamedays/gamedays'
import type { TriggerSourceKey } from 'types/api/runs-common'
import type { BaseGraph, GraphNode } from 'types/api/scenarios/scenario'

export interface BaseScenarioRunGraph extends BaseGraph {
  nodes_recursive?: GraphNode[]
  current_id?: string
}

export type ScenarioRunGraph = BaseScenarioRunGraph &
  CamelCasedProperties<BaseScenarioRunGraph>

export interface ScenarioRunInput {
  hypothesis?: string
}

export type ScenarioRunStatus = 'Passed' | 'Unsure' | 'Failed'

export interface ScenarioRunResults {
  incidentDetected?: boolean
  incidentMitigated?: boolean
  status?: ScenarioRunStatus
}

export enum ScenarioRunStage {
  SETUP = 'Setup',
  ACTIVE = 'Active',
  SETUP_FAILED = 'SetupFailed',
  FAILED = 'Failed',
  HALTED = 'Halted',
  HALTING = 'Halting',
  COMPLETED = 'Completed'
}

export interface ScenarioRunStageInfo {
  currentNodeId?: string
  stage: ScenarioRunStage
  stageMetadata: {
    failedReason?: string
    haltReason?: string
    [key: string]: unknown
  }
}

// This is sometimes camelCase and sometimes snake_case depending on the endpoint :(
export interface ScenarioRunResponse {
  created_at: string
  created_by: string
  created_by_first_name?: string
  created_by_last_name?: string
  create_source: RequestSource
  description?: string
  end_time?: string
  game_day_info?: {
    guid: string
    name: string
  }
  graph: ScenarioRunGraph
  hypothesis?: string
  large_scale_test_id?: string | null
  name: string
  org_id?: string
  pass_criteria: ScenarioPassCriteria
  reliability_test_id?: string
  resolved_targets?: Array<Array<{ [key: string]: any }>>
  result_flags?: { [key: string]: boolean }
  result_notes?: string
  results?: ScenarioRunResults
  run_number?: number
  scenario_id?: string
  scenario_run_images?: string[]
  schedule_id?: string
  service_id?: string
  stage_info?: ScenarioRunStageInfo
  step_summaries?: Array<{ [key: string]: any }>
  trigger_source?: TriggerSourceKey
  updated_at?: string
}

// accounting for possibility of it being returned with either casing
export type ScenarioRun = ScenarioRunResponse &
  CamelCasedProperties<ScenarioRunResponse>

/*
 * Scenario Run Metrics related types
 */

export enum ScenarioRunMetricsState {
  DISABLED = 'DISABLED',
  PENDING = 'PENDING',
  NO_DATA = 'NO_DATA',
  PARTIAL = 'PARTIAL',
  COMPLETE = 'COMPLETE'
}

interface MetricsRange {
  end: number
  start: number
}

interface MetricData {
  timestamp: number
  value: number
}

interface MetricLine {
  label: string
  value: number
}

export interface ScenarioRunMetrics {
  metrics: {
    attack_range: MetricsRange
    chart_title: string
    disabled_reason: string | null
    infra_command_type: string
    lines: MetricLine[]
    metric_data: Record<string, MetricData[]>
    range: MetricsRange
    state: ScenarioRunMetricsState
  }
  taskId: string
}
